import React from "react";

const THEME = "#8b1634";

const ROLES = [
  {
    key: "admin",
    label: "Admin",
    icon: "fa-solid fa-user-shield",
    color: "#8b1634",
    bg: "#fbeef2",
    desc: "Full control of the whole system — every page, every setting and all logs.",
  },
  {
    key: "subAdmin",
    label: "Sub Admin",
    icon: "fa-solid fa-user-gear",
    color: "#1d4ed8",
    bg: "#eff6ff",
    desc: "Almost like an Admin. Manages Managers & Secret Keys, but cannot manage Sub Admins / Directors, App Update or system logs.",
  },
  {
    key: "manager",
    label: "Manager",
    icon: "fa-solid fa-user-tie",
    color: "#047857",
    bg: "#ecfdf5",
    desc: "Runs day-to-day work — sites, forms, fatigue, assets, team. No Settings / user management.",
  },
];

// value codes: "full" | "no"
const SECTIONS = [
  {
    title: "Dashboard & Operations",
    rows: [
      { f: "Main Dashboard", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Sites", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Safety Forms (SF-series)", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Asset Management (assets, QR labels, fault tickets)", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Team", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Calendar View", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Southeastern / UKPN entries", admin: "full", subAdmin: "full", manager: "full" },
    ],
  },
  {
    title: "Creating / Adding Records (who can create)",
    rows: [
      { f: "Create a Shift (Shift Entry)", admin: "no", subAdmin: "no", manager: "full" },
      { f: "Create / add a Site", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Create an Asset / Fault Ticket", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Create D-Fatigue Management Plan", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Fill / create a Safety Form", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Create App User (field worker)", admin: "full", subAdmin: "full", manager: "no" },
      { f: "Create a Manager", admin: "full", subAdmin: "full", manager: "no" },
      { f: "Create a Secret Key", admin: "full", subAdmin: "full", manager: "no" },
      { f: "Create a Sub Admin", admin: "full", subAdmin: "no", manager: "no" },
      { f: "Create a Director", admin: "full", subAdmin: "no", manager: "no" },
    ],
  },
  {
    title: "Fatigue Management",
    rows: [
      { f: "Work Sheets & Shift Entry (view / manage)", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Exceedance Forms (review / approve / reject)", admin: "full", subAdmin: "full", manager: "full" },
      { f: "Billing Report", admin: "full", subAdmin: "full", manager: "full" },
    ],
  },
  {
    title: "Administration (Settings)",
    rows: [
      { f: "App Users (field workers)", admin: "full", subAdmin: "full", manager: "no" },
      { f: "Managers", admin: "full", subAdmin: "full", manager: "no" },
      { f: "Secret Key Manager", admin: "full", subAdmin: "full", manager: "no" },
      { f: "Sub Admins", admin: "full", subAdmin: "no", manager: "no" },
      { f: "Director", admin: "full", subAdmin: "no", manager: "no" },
      { f: "App Update", admin: "full", subAdmin: "no", manager: "no" },
      { f: "Logs (Form Error / Api Error / Form Submissions)", admin: "full", subAdmin: "no", manager: "no" },
    ],
  },
];

function Cell({ value }) {
  const map = {
    full: { label: "Full", icon: "fa-solid fa-circle-check", color: "#047857", bg: "#ecfdf5", border: "#a7f3d0" },
    view: { label: "View only", icon: "fa-solid fa-eye", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
    app: { label: "Mobile app", icon: "fa-solid fa-mobile-screen-button", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
    no: { label: "—", icon: "fa-solid fa-minus", color: "#94a3b8", bg: "transparent", border: "transparent" },
  };
  const c = map[value] || map.no;
  if (value === "no") return <span style={{ color: "#cbd5e1" }}><i className={c.icon} /></span>;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <i className={c.icon} style={{ fontSize: 11 }} />
      {c.label}
    </span>
  );
}

export default function RolesPermissionsPage() {
  return (
    <div style={{ padding: "8px 4px" }}>
      <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: 24 }}>
        <h2 style={{ margin: 0, color: THEME, fontWeight: 700, fontSize: 24 }}>
          <i className="fa-solid fa-user-lock" style={{ marginRight: 10 }} />
          Roles &amp; Permissions
        </h2>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px", marginTop: 14, marginBottom: 22 }}>
          <i className="fa-solid fa-circle-info" style={{ color: "#b45309", marginTop: 2 }} />
          <div style={{ color: "#92400e", fontSize: 13, lineHeight: 1.5 }}>
            <b>Note:</b> Creating a <b>Shift</b> is restricted to the <b>Manager</b> role only — Admin and Sub Admin can view shifts but cannot create them.
          </div>
        </div>

        {/* Role cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14, marginBottom: 26 }}>
          {ROLES.map((r) => (
            <div key={r.key} style={{ border: `1px solid ${r.color}22`, background: r.bg, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ width: 38, height: 38, borderRadius: 10, background: r.color, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                  <i className={r.icon} />
                </span>
                <span style={{ fontWeight: 700, color: r.color, fontSize: 15 }}>{r.label}</span>
              </div>
              <div style={{ color: "#475569", fontSize: 13, lineHeight: 1.45 }}>{r.desc}</div>
            </div>
          ))}
        </div>

        {/* Permissions table */}
        <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead>
              <tr style={{ background: "#faf3f6" }}>
                <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 13, color: THEME, minWidth: 260 }}>Feature</th>
                {ROLES.map((r) => (
                  <th key={r.key} style={{ textAlign: "center", padding: "12px 14px", fontSize: 13, color: r.color }}>
                    <i className={r.icon} style={{ marginRight: 6 }} />
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map((section) => (
                <React.Fragment key={section.title}>
                  <tr>
                    <td colSpan={4} style={{ background: "#8b16340d", color: THEME, fontWeight: 700, fontSize: 13, padding: "10px 14px", borderTop: "1px solid #f1e3e8" }}>
                      {section.title}
                    </td>
                  </tr>
                  {section.rows.map((row, i) => (
                    <tr key={row.f} style={{ borderTop: "1px solid #f5f5f5", background: i % 2 ? "#fcfcfd" : "#fff" }}>
                      <td style={{ padding: "11px 14px", fontSize: 13, color: "#334155" }}>{row.f}</td>
                      <td style={{ padding: "11px 14px", textAlign: "center" }}><Cell value={row.admin} /></td>
                      <td style={{ padding: "11px 14px", textAlign: "center" }}><Cell value={row.subAdmin} /></td>
                      <td style={{ padding: "11px 14px", textAlign: "center" }}><Cell value={row.manager} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 16, fontSize: 12, color: "#64748b" }}>
          <span><Cell value="full" /> &nbsp;= Full access</span>
          <span style={{ color: "#cbd5e1" }}><i className="fa-solid fa-minus" /> &nbsp;<span style={{ color: "#64748b" }}>= No access</span></span>
        </div>
      </div>
    </div>
  );
}
